import type { Chart, ChartData, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useMemo, useRef } from 'react';

import { CenterFlex } from '@/components/base';
import { CHART_METRIC_VIEW_TYPE } from '@/entities/chart/model/constants';
import useEventPlugin from '@/features/(charts)/chart-renderer/hooks/use-chart-event-plugin';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import { PieChart as SharedPieChart, useChartTooltip } from '@/shared/ui/charts';

import ChartCustomTooltip from '../chart-custom-tooltip/ChartCustomTooltip';
import useChartAccData from './useChartAccData';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
  yAxisTitle?: string;
};

const PieChart = (props: Props) => {
  const color = useColor();
  const chartRef = useRef<Chart>(null);

  const eventPlugin = useEventPlugin({
    meta: props.chart,
    dashboardCardInfo: props.dashboardCardInfo,
  });
  const { tooltipData, chartTooltip } = useChartTooltip({
    chart: chartRef.current,
  });
  const { accChartData } = useChartAccData(props);

  const doughnutChartOptions: ChartOptions<'doughnut'> = useMemo(() => {
    return {
      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'click'],
        },
        legend: {
          labels: {
            color: color.gray700,
          },
          position: 'right',
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
        datalabels: {
          color: color.gray100,
          display: function (context: any) {
            var dataset = context.dataset;
            var total = dataset.data.reduce(
              (acc: number, curr: number) => acc + curr,
              0,
            );
            var value = dataset.data[context.dataIndex];
            return value > total * 0.05;
          },
          formatter(value, context: any) {
            var dataset = context.dataset;
            var total = dataset.data.reduce(
              (acc: number, curr: number) => acc + curr,
              0,
            );
            if (
              props.chart.series[0]?.options?.view_type ===
              CHART_METRIC_VIEW_TYPE.PERCENTAGE
            ) {
              return `${((value / total) * 100).toFixed(1)}%`;
            }
            return value;
          },
          labels: {
            value: {
              color: color.FL100,
            },
          },
        },
      },
    };
  }, [
    chartTooltip,
    color.FL100,
    color.gray100,
    color.gray700,
    props.chart.series,
  ]);

  return (
    <CenterFlex className="relative w-full h-full">
      <CenterFlex className="relative w-full max-w-[800px] flex-1 h-full">
        <SharedPieChart
          data={accChartData as ChartData<'doughnut'>}
          options={doughnutChartOptions}
          chartRef={chartRef as React.RefObject<Chart<'doughnut'>>}
          plugins={[eventPlugin, ChartDataLabels]}
        />
        {tooltipData && (
          <ChartCustomTooltip
            tooltip={tooltipData}
            meta={props.chart}
            tableData={props.tableData}
            dashboardCardInfo={props.dashboardCardInfo}
          />
        )}
      </CenterFlex>
    </CenterFlex>
  );
};

export default PieChart;
